import { useEffect } from 'react';
import { z } from '@customerio/hydra-validation';
import { sentryCaptureException } from '@/utils/sentry';
import { useAddonPurchase } from '../hooks/use-addon-purchase';
import { addonRequestSchema, type AddonRequest } from '../utils/addon-purchase';

/**
 * Window bridge for add-on purchases.
 *
 * Exposes `window.__cio_addons` so the embedded Ember app can open the add-on
 * purchase modal, which only renders inside Hydra's app layout.
 */
interface CioAddonsBridge {
  /** `request` comes from another repo that deploys on its own, so it's checked before use. */
  open: (request: unknown) => Promise<{ isPurchased: boolean }>;
}

declare global {
  interface Window {
    __cio_addons?: CioAddonsBridge;
  }
}

/**
 * A stored-profile packs request without the account's numbers. Ember does not know them, so the
 * bridge adds them. Ember's segment CSV import sends this form. When that page moves to Hydra, it
 * can use `useOpenStoragePacks`, and this form can go.
 */
const storedProfilesWithoutUsageSchema = z.object({ key: z.literal('stored-profiles') });

const bridgeRequestSchema = z.union([addonRequestSchema, storedProfilesWithoutUsageSchema]);

export type BridgeStorage = { stored: number; limit: number };

type AddonPurchaseBridgeProps = {
  /** The account's stored profiles against its limit. `null` while unknown, or off Flex. */
  storage?: BridgeStorage | null;
  /** Runs after a stored-profile pack is bought, so Hydra's own views of the account update. */
  onStoragePurchased?: () => Promise<void>;
};

export function AddonPurchaseBridge({ storage = null, onStoragePurchased }: AddonPurchaseBridgeProps) {
  const { openAddon } = useAddonPurchase();

  useEffect(() => {
    window.__cio_addons = {
      open: async (request) => {
        const parsed = bridgeRequestSchema.safeParse(request);
        if (!parsed.success) {
          sentryCaptureException(new Error('Refused a malformed add-on request from an embedded app'), {
            extra: { request, issues: parsed.error.issues },
          });
          return { isPurchased: false };
        }

        const fullRequest = withStorage(parsed.data, storage);
        if (fullRequest == null) {
          // Rejected, so Ember tells the user the packs couldn't open and reports it.
          throw new Error("The account's storage isn't loaded, so the stored-profile packs can't open");
        }

        const result = await openAddon({ request: fullRequest });
        const isPurchased = result?.isPurchased === true;
        if (isPurchased && fullRequest.key === 'stored-profiles' && onStoragePurchased != null) {
          await onStoragePurchased();
        }
        return { isPurchased };
      },
    };

    return () => {
      delete window.__cio_addons;
    };
  }, [openAddon, storage, onStoragePurchased]);

  return null;
}

/** The request with the account's storage added where it left it out. `null` when storage is unknown. */
function withStorage(request: z.infer<typeof bridgeRequestSchema>, storage: BridgeStorage | null): AddonRequest | null {
  if (request.key !== 'stored-profiles') return request;
  if ('stored' in request) return request;
  if (storage == null) return null;
  return { key: 'stored-profiles', stored: storage.stored, limit: storage.limit };
}
